import { useCallback, useEffect, useRef, useState } from "react";
import type { Translate } from "../i18n/hooks.ts";
import { translateError } from "../i18n/hooks.ts";
import {
  deleteSavedProject,
  loadSavedProject,
  saveProject,
  getProjectMeta,
  newProjectId,
  renameSavedProject,
  buildProjectMeta,
  buildProjectData,
  type MitecoOrigin,
  type ProjectData,
  type ProjectMeta,
} from "../model/storage.ts";
import { exportProject, importProject, exportedFileName } from "../model/exportProject.ts";
import { buildProjectCsvZip, csvExportFileName } from "../model/exportCsvProject.ts";
import { driveSyncStore } from "../model/driveSyncStore.ts";
import type { ReadableFolder } from "../model/fsReadable.ts";
import { loadSubBasinLayer, type SubBasinLayer } from "../model/gisLayer.ts";
import type { ExampleProject } from "../model/examples.ts";
import { aemetJobStore } from "../model/aemet/aemetJobStore.ts";
import { openProject, listEhdFiles, type FoundEhd, type LoadedProject } from "../model/project.ts";
import { hasCsvProjectFiles, openCsvProject } from "../model/importCsvProject.ts";
import type { Language } from "../model/language.ts";
import type { EvalHidProject } from "../model/types.ts";
import { projectIdFromLocation, projectUrl } from "../model/projectRoute.ts";
import {
  EMPTY_AQUIFERS_LAYER,
  EMPTY_OUTLET_POINTS_LAYER,
  loadedState,
  type State,
} from "./appState.ts";
import { useSavedProjectsList } from "./useSavedProjectsList.ts";

/**
 * Owns the whole lifecycle of "the currently open project" — the `state`/
 * `setState` pair itself (App.tsx used to hold this directly), the list of
 * saved projects, and every handler that opens/saves/renames/exports/
 * closes/deletes a project. Extracted from App.tsx (which had grown to
 * ~1700 lines with ~50 handlers, all in one component scope — see
 * project_evalhid_code_audit.md) as the first, most self-contained slice:
 * everything here is genuinely about project persistence, nothing here is
 * needed by the OTHER handler groups still in App.tsx (calculation, panel
 * toggles, MITECO, drawing...) except the `state`/`setState` pair itself,
 * which those still read/write directly via this hook's return value.
 *
 * Takes `t`/`language` as parameters rather than owning them — both are
 * used well beyond persistence (basically every handler group in App.tsx
 * needs `t`), so they stay owned by App.tsx itself.
 */
export function withProjectRenamed(current: State, name: string): State {
  return current.phase === "loaded" ? { ...current, name } : current;
}

/**
 * Reads a full project from a folder (a real one, or an example's — see
 * `loadFrom`'s own comment) and resolves what `State` it should become,
 * without touching React or IndexedDB — pulled out as a plain async
 * function (rather than staying inline in `loadFrom`) so it's directly
 * unit-testable with a mock folder (test/fsMock.ts) and the real
 * ModeloTuria fixture, same as project.test.ts already does for
 * `openProject` itself.
 */
export async function resolveProjectFromFolder(
  t: Translate,
  language: Language,
  root: ReadableFolder,
  adjustProject?: (project: EvalHidProject, t: Translate) => EvalHidProject,
  ehdFileName?: string
): Promise<
  | { ok: true; loadedProject: LoadedProject; gisLayer: SubBasinLayer | null }
  | { ok: false; message: string }
> {
  try {
    const originalLoaded = await openProject(t, language, root, ehdFileName);
    const loadedProject = adjustProject
      ? { ...originalLoaded, project: adjustProject(originalLoaded.project, t) }
      : originalLoaded;
    // The GIS layer is an extra: if it fails or is missing, the rest of the
    // project is shown anyway — still logged (deep-review, 2026-08-29) so a
    // real failure (shapefile parse crash, malformed .dbf, a projection
    // error) leaves a trace instead of just silently showing no polygons.
    const gisLayer = await loadSubBasinLayer(root, loadedProject.project).catch((err) => {
      console.error("Could not load the GIS layer:", err);
      return null;
    });
    return { ok: true, loadedProject, gisLayer };
  } catch (err) {
    return { ok: false, message: translateError(err, t) };
  }
}

/**
 * Same idea as resolveProjectFromFolder, for a folder with no .ehd — just
 * the CSV files exportCsvProject.ts writes (see importCsvProject.ts). Always
 * `gisLayer: null` (the CSV format has no geometry): appState.ts's own
 * `mapView: gisLayer !== null && ...` already turns that into card view
 * automatically, so there's nothing extra to decide here.
 *
 * Not exported (unlike resolveProjectFromFolder): the logic worth testing on
 * its own lives entirely in openCsvProject itself (see
 * test/importCsvProject.test.ts) — this is just the try/catch shape around
 * it, exercised through openFolder()/loadFromCsv.
 */
async function resolveCsvProjectFromFolder(
  t: Translate,
  language: Language,
  root: ReadableFolder
): Promise<{ ok: true; loadedProject: LoadedProject } | { ok: false; message: string }> {
  try {
    const loadedProject = await openCsvProject(t, language, root);
    return { ok: true, loadedProject };
  } catch (err) {
    return { ok: false, message: translateError(err, t) };
  }
}

/**
 * Reads an already-saved project from IndexedDB (storage.ts) and resolves
 * what `State` it should become — pulled out for the same reason as
 * `resolveProjectFromFolder`: directly unit-testable (with fake-indexeddb,
 * same convention as storage.test.ts) without a React environment.
 */
export async function resolveSavedProject(
  id: string,
  t: Translate
): Promise<{ ok: true; state: State } | { ok: false; message: string }> {
  try {
    const [data, meta] = await Promise.all([loadSavedProject(id), getProjectMeta(id)]);
    if (!data) {
      return { ok: false, message: t("app.savedProjectNotFoundError") };
    }
    return {
      ok: true,
      state: loadedState({
        projectId: id,
        name: meta?.name ?? t("app.defaultProjectName"),
        ephemeral: meta?.ephemeral ?? false,
        loadedProject: { project: data.project, series: data.series, notices: [] },
        gisLayer: data.gisLayer,
        outletPointsLayer: data.outletPointsLayer,
        aquifersLayer: data.aquifersLayer,
        genericLayers: data.genericLayers ?? [],
        aemetProvenance: data.aemetProvenance ?? {},
        mitecoOrigin: data.mitecoOrigin ?? {},
        observedFlow: data.observedFlow ?? null,
      }),
    };
  } catch (err) {
    return { ok: false, message: translateError(err, t) };
  }
}

/**
 * "Save copy as": builds the new `State` plus the exact `ProjectMeta`/
 * `ProjectData` pair to persist for it — pure (given a `projectId`, since
 * that itself comes from `newProjectId()`, not from anything derivable from
 * `current`) and thus directly testable, unlike the actual `saveProject()`
 * IndexedDB write it feeds into. Always `ephemeral: false`, even when
 * `current` came from an example — the one way an example ends up really
 * persisted (see `ProjectMeta.ephemeral`'s own comment in storage.ts).
 */
export function buildSaveAsPayload(
  current: State,
  projectId: string,
  name: string
): { state: State; meta: ProjectMeta; data: ProjectData } | null {
  if (current.phase !== "loaded") return null;
  const {
    loadedProject,
    gisLayer,
    outletPointsLayer,
    aquifersLayer,
    genericLayers,
    aemetProvenance,
    mitecoOrigin,
    observedFlow,
  } = current;
  return {
    state: loadedState({
      projectId,
      name,
      ephemeral: false,
      loadedProject,
      gisLayer,
      outletPointsLayer,
      aquifersLayer,
      genericLayers,
      aemetProvenance,
      mitecoOrigin,
      observedFlow,
    }),
    meta: buildProjectMeta(projectId, name, false),
    data: buildProjectData(projectId, loadedProject, {
      gisLayer,
      outletPointsLayer,
      aquifersLayer,
      genericLayers,
      aemetProvenance,
      mitecoOrigin,
      observedFlow,
    }),
  };
}

/** Common return shape for both export actions below — lets TopBar.tsx show a translated error instead of a silent console.error, and know when to stop showing its own "generating…" state. */
export type ExportOutcome = { ok: true } | { ok: false; error: string };

export function useProjectPersistence(t: Translate, language: Language) {
  // Lazy initializer (function form): reads window.location.pathname exactly
  // once, at mount — a "/p/<id>" URL starts the app straight at "loading"
  // instead of flashing Landing.tsx first and swapping to loading a moment
  // later. The actual async resolve happens in the mount effect below, once
  // `openSaved` exists.
  const [state, setState] = useState<State>(() =>
    projectIdFromLocation() !== null ? { phase: "loading" } : { phase: "initial" }
  );
  /**
   * Always-current mirror of `state`, read by the autosave effect below at
   * FIRE time rather than from its own closure — deliberately kept in sync
   * on every render (not via a `useEffect`, to avoid one extra render's
   * worth of staleness). Without this, a rename (or anything else changing
   * `name`/`ephemeral`/`projectId` alone) would silently be reverted by an
   * already-armed autosave timer that still has the pre-rename name in its
   * closure — real bug found in production-review, see the autosave
   * effect's own comment for why those 3 fields aren't in its dependency
   * array in the first place.
   */
  const stateRef = useRef(state);
  stateRef.current = state;
  /**
   * Set by `requestImmediateSave` (App.tsx, wired to aemetJobStore.ts's
   * `onBatchEnd`) to make the very next run of the debounced autosave effect
   * below skip its 1s wait — read there.
   *
   * Real bug found live: a long AEMET/ROCIO batch (Duero: 1728 sub-basins)
   * keeps `projectToSave`/`aemetProvenanceToSave` changing every
   * `AEMET_APPLY_THROTTLE_MS` (useProjectDataUpdates.ts) for as long as it
   * runs — each change resets the autosave's own 1s timer, so nothing
   * actually reaches IndexedDB until a full second passes with NO new apply
   * arriving. For a batch that keeps producing applies faster than that
   * (plausible for hundreds/thousands of sub-basins), that's the batch's
   * entire duration: extending series, then calculating, then refreshing
   * shortly after — exactly what was reported — can lose it all, even
   * though `onBatchEnd` already flushes the buffered React state via
   * `flushAemetApply()`. Flushing only gets the FINAL data into `state`; it
   * doesn't bypass the debounce that still gates writing it to IndexedDB.
   *
   * A plain boolean is enough (not a counter/queue): the two current
   * callers of `requestImmediateSave` (App.tsx) always set it right after
   * `flushAemetApply()`, in the same synchronous batch-end callback — the
   * autosave effect below reads and resets it the very next time it runs,
   * which is exactly the run triggered by that flush's own `setState`
   * landing (its dependencies always change on a real batch, since
   * `onBatchEnd` only fires after at least one sub-basin applied).
   */
  const forceImmediateSaveRef = useRef(false);
  const requestImmediateSave = useCallback(() => {
    forceImmediateSaveRef.current = true;
  }, []);
  /** Only filled in if the folder chosen in "Open project" has more than one .ehd (counting subfolders) — see openFolder()/ChooseEhdDialog.tsx. */
  const [choosingEhd, setChoosingEhd] = useState<FoundEhd[] | null>(null);
  /** "Close project" was clicked while an AEMET download was still running — see aemetJobStore.ts/ConfirmCloseWithJobsDialog.tsx. */
  const [confirmCloseWithJobs, setConfirmCloseWithJobs] = useState(false);

  const {
    savedProjects,
    reloadSavedProjects,
    driveUnsyncedProjects,
    uploadUnsyncedToDrive,
    dismissUnsyncedDriveDialog,
    resolveDriveConflict,
  } = useSavedProjectsList();

  /**
   * Last step common to the four ways of having a project open (a real
   * folder, an example, the "create new" wizard, or reopening one already
   * saved): enters `phase: "loaded"` and saves it right away to IndexedDB
   * (see storage.ts) — no need to wait for the debounced autosave further
   * down for it to already show up in "Your projects" if the user closes
   * the tab right away. From here on the original folder/handle is never
   * touched again: everything lives in IndexedDB.
   */
  const finishLoadingProject = useCallback(
    (
      loadedProject: LoadedProject,
      gisLayer: SubBasinLayer | null,
      name: string,
      ephemeral: boolean,
      /** Only set by the "MITECO" wizard tab (Step1SubBasins.tsx) — the sub-basins it just created came from a real MITECO import, not a shapefile/manual entry, so `mitecoOrigin` starts already filled in instead of empty. */
      mitecoOrigin: Record<number, MitecoOrigin> = {},
      /** Also only set by that same tab — see NewProject.tsx's `finishFromMiteco`. */
      showMitecoAemetBanner = false
    ) => {
      const projectId = newProjectId();
      setState(
        loadedState({
          projectId,
          name,
          ephemeral,
          loadedProject,
          gisLayer,
          outletPointsLayer: EMPTY_OUTLET_POINTS_LAYER,
          aquifersLayer: EMPTY_AQUIFERS_LAYER,
          genericLayers: [],
          aemetProvenance: {},
          mitecoOrigin,
          observedFlow: null,
          mitecoAemetBannerVisible: showMitecoAemetBanner,
        })
      );
      const meta = buildProjectMeta(projectId, name, ephemeral);
      const data = buildProjectData(projectId, loadedProject, {
        gisLayer,
        outletPointsLayer: EMPTY_OUTLET_POINTS_LAYER,
        aquifersLayer: EMPTY_AQUIFERS_LAYER,
        genericLayers: [],
        aemetProvenance: {},
        mitecoOrigin,
        observedFlow: null,
      });
      saveProject(meta, data)
        .then(() => {
          reloadSavedProjects();
          driveSyncStore.queueSync(meta, data);
        })
        .catch((err) => console.error("Could not save the just-opened project:", err));
    },
    [reloadSavedProjects]
  );

  // Shared between "open project" (real folder, showDirectoryPicker) and
  // "load example" (folder backed by fetch, see examples.ts): the two only
  // differ in where the ReadableFolder comes from (and, for examples, in
  // the in-memory adjustment that fills in whatever the real .ehd is
  // missing — see ExampleProject.adjustProject). Each caller decides the
  // name (the real folder's name, or the example's title) because
  // ReadableFolder doesn't expose it generically (examples aren't a real folder).
  const loadFrom = useCallback(
    async (
      root: ReadableFolder,
      name: string,
      ephemeral: boolean,
      adjustProject?: (project: EvalHidProject, t: Translate) => EvalHidProject,
      /** Only needed when the folder has more than one .ehd — see `choosingEhd` further down. */
      ehdFileName?: string
    ) => {
      setState({ phase: "loading" });
      const result = await resolveProjectFromFolder(t, language, root, adjustProject, ehdFileName);
      if (result.ok) {
        finishLoadingProject(result.loadedProject, result.gisLayer, name, ephemeral);
      } else {
        setState({ phase: "error", message: result.message });
      }
    },
    [finishLoadingProject, t, language]
  );

  /** Same as loadFrom, for a folder with no .ehd — see resolveCsvProjectFromFolder/importCsvProject.ts. Always gisLayer: null. */
  const loadFromCsv = useCallback(
    async (root: ReadableFolder, name: string, ephemeral: boolean) => {
      setState({ phase: "loading" });
      const result = await resolveCsvProjectFromFolder(t, language, root);
      if (result.ok) {
        finishLoadingProject(result.loadedProject, null, name, ephemeral);
      } else {
        setState({ phase: "error", message: result.message });
      }
    },
    [finishLoadingProject, t, language]
  );

  const openSaved = useCallback(
    async (id: string) => {
      setState({ phase: "loading" });
      const result = await resolveSavedProject(id, t);
      setState(result.ok ? result.state : { phase: "error", message: result.message });
    },
    [t]
  );

  // Restores the project named in the URL, if any — the async half of the
  // lazy `useState` initializer above. Mount-only: a LATER change to the
  // URL (browser Back/Forward) is handled by the popstate listener further
  // down, not by re-running this. `openSaved`'s own failure path already
  // produces the right UX for a dead/unknown id (`{phase:"error", message:
  // t("app.savedProjectNotFoundError")}`, which App.tsx renders through
  // Landing.tsx like any other error) — nothing special needed for an
  // ephemeral/example id that's since been purged (purgeEphemeralProjects,
  // storage.ts), it just falls into that same fallback.
  useEffect(() => {
    const id = projectIdFromLocation();
    if (id !== null) openSaved(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflects `state` into the URL — the ONLY place in this file that calls
  // history.pushState/replaceState, so every other state transition below
  // (saveAs, close, delete, the wizard via finishLoadingProject...) is
  // picked up automatically just by changing `state`, with nothing extra to
  // thread through each of them individually.
  const activeProjectId = state.phase === "loaded" ? state.projectId : null;
  const hasSyncedUrlRef = useRef(false);
  useEffect(() => {
    // Still resolving (this mount's own restore, or any other project-open
    // in flight) — nothing settled yet to reflect. Without this guard EVERY
    // project open would flash the URL to "/" and then to "/p/<id>".
    if (state.phase === "loading") return;
    const urlId = projectIdFromLocation();
    if (activeProjectId === urlId) {
      hasSyncedUrlRef.current = true;
      return;
    }
    const url = projectUrl(activeProjectId);
    if (!hasSyncedUrlRef.current) {
      // First real settle since mount (restoring from the URL, or clearing
      // a dead one) — never worth its own "Back" history entry.
      window.history.replaceState(null, "", url);
    } else {
      window.history.pushState(null, "", url);
    }
    hasSyncedUrlRef.current = true;
  }, [state.phase, activeProjectId]);

  const renameSaved = useCallback(
    (id: string, name: string) => {
      renameSavedProject(id, name)
        .then(reloadSavedProjects)
        .catch((err) => console.error("Could not rename the project:", err));
    },
    [reloadSavedProjects]
  );

  // "Save copy as": creates a new record (its own id) from the current
  // project and switches to working on that copy, without touching the
  // original. Also the only way for an example to end up really persisted
  // (see ProjectMeta.ephemeral in storage.ts).
  const saveAs = useCallback(
    (name: string) => {
      const payload = buildSaveAsPayload(state, newProjectId(), name);
      if (!payload) return;
      // Real bug found live: this used to switch to the copy IMMEDIATELY,
      // before knowing whether `saveProject` below actually succeeded — at
      // Duero scale a failed write (out of memory) left the user "inside" a
      // copy that was never really persisted (or, per `loadSavedProject`'s
      // own fallback, one missing its series specifically), discovered only
      // much later. Now only switches once the write is confirmed; on
      // failure the original project stays open, with the error surfaced
      // instead of a silent `console.error`.
      saveProject(payload.meta, payload.data)
        .then(() => {
          setState(payload.state);
          reloadSavedProjects();
          driveSyncStore.queueSync(payload.meta, payload.data);
        })
        .catch((err) => {
          console.error("Could not save the project copy:", err);
          setState((c) =>
            c.phase === "loaded"
              ? { ...c, saveError: t("app.saveAsError", { message: translateError(err, t) }) }
              : c
          );
        });
    },
    [state, reloadSavedProjects, t]
  );

  /**
   * Renames the open project in place (same id) — unlike saveAs, this
   * doesn't create a new copy. Only makes sense if it's already really
   * saved (not ephemeral, see TopBar.tsx: the button is disabled in that case).
   */
  const renameCurrentProject = useCallback(
    (name: string) => {
      if (state.phase !== "loaded") return;
      const { projectId } = state;
      setState((current) => withProjectRenamed(current, name));
      renameSavedProject(projectId, name)
        .then(reloadSavedProjects)
        .catch((err) => console.error("Could not rename the project:", err));
    },
    [state, reloadSavedProjects]
  );

  /**
   * Downloads the open project as a single `.ehdz` file (portable backup
   * copy, see exportProject.ts) — doesn't touch IndexedDB or the current
   * project, only triggers the browser download. Returns (rather than
   * throws) an `ExportOutcome` so the caller can show progress/errors
   * without its own try/catch — see TopBar.tsx's own use of this.
   */
  const exportCurrentProject = useCallback(async (): Promise<ExportOutcome> => {
    if (state.phase !== "loaded") return { ok: true };
    const {
      projectId,
      name,
      loadedProject,
      gisLayer,
      outletPointsLayer,
      aquifersLayer,
      genericLayers,
      aemetProvenance,
      mitecoOrigin,
      observedFlow,
    } = state;
    try {
      const blob = await exportProject(name, {
        id: projectId,
        project: loadedProject.project,
        series: loadedProject.series,
        gisLayer,
        outletPointsLayer,
        aquifersLayer,
        genericLayers,
        aemetProvenance,
        mitecoOrigin,
        observedFlow,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = exportedFileName(name);
      link.click();
      URL.revokeObjectURL(url);
      return { ok: true };
    } catch (err) {
      console.error("Could not export the project:", err);
      return { ok: false, error: translateError(err, t) };
    }
  }, [state, t]);

  /**
   * Downloads the project as the same set of CSV files the original desktop
   * app writes for its calculation engine, bundled into a .zip — see
   * exportCsvProject.ts. Unlike exportCurrentProject's .ehdz (a portable
   * backup of THIS app's own data), the point here is giving the user files
   * in the original's own format to do whatever they want with.
   */
  const exportCurrentProjectAsCsv = useCallback(async (): Promise<ExportOutcome> => {
    if (state.phase !== "loaded") return { ok: true };
    try {
      const blob = await buildProjectCsvZip(
        t,
        state.loadedProject.project,
        state.loadedProject.series
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = csvExportFileName(state.name);
      link.click();
      URL.revokeObjectURL(url);
      return { ok: true };
    } catch (err) {
      console.error("Could not export the project as CSV:", err);
      return { ok: false, error: translateError(err, t) };
    }
  }, [state, t]);

  /**
   * Imports a `.ehdz` exported before (see exportProject.ts): saves it as a
   * new, really-saved project (`ephemeral: false`, its own id) and opens it
   * — same pattern as openSaved/finishLoadingProject, but the source is an
   * uploaded file instead of IndexedDB or a real folder.
   */
  const importFromFile = useCallback(
    async (file: File) => {
      setState({ phase: "loading" });
      try {
        const { name, data } = await importProject(file);
        const projectId = newProjectId();
        const dataWithId = { ...data, id: projectId };
        const meta = buildProjectMeta(projectId, name, false);
        await saveProject(meta, dataWithId);
        driveSyncStore.queueSync(meta, dataWithId);
        setState(
          loadedState({
            projectId,
            name,
            ephemeral: false,
            loadedProject: { project: dataWithId.project, series: dataWithId.series, notices: [] },
            gisLayer: dataWithId.gisLayer,
            outletPointsLayer: dataWithId.outletPointsLayer,
            aquifersLayer: dataWithId.aquifersLayer,
            genericLayers: dataWithId.genericLayers ?? [],
            aemetProvenance: dataWithId.aemetProvenance ?? {},
            mitecoOrigin: dataWithId.mitecoOrigin ?? {},
            observedFlow: dataWithId.observedFlow ?? null,
          })
        );
        reloadSavedProjects();
      } catch (err) {
        setState({ phase: "error", message: translateError(err, t) });
      }
    },
    [reloadSavedProjects, t]
  );

  // Deletes the open project from IndexedDB and goes back to the start
  // screen — unlike closeProject, a prior save makes no sense here (it's
  // being deleted anyway).
  const deleteCurrentProject = useCallback(() => {
    if (state.phase !== "loaded") return;
    const { projectId } = state;
    aemetJobStore.cancelAll();
    driveSyncStore.deleteRemote(projectId);
    setState({ phase: "initial" });
    deleteSavedProject(projectId)
      .catch((err) => console.error("Could not delete the project:", err))
      .finally(reloadSavedProjects);
  }, [state, reloadSavedProjects]);

  // Goes back to the start screen: forces an immediate save (without
  // waiting for the debounced autosave below) before switching phase, so
  // the last changes aren't lost if the user closes right after editing.
  // For an example (ephemeral) this still saves to its "orphan" row in
  // IndexedDB (purged on next app load, see purgeEphemeralProjects) — it
  // doesn't prevent losing the work if a copy is never saved, but that
  // warning is constant (ExampleBanner, see further down), not only on
  // close: the most common way to lose it is closing the tab without going
  // through here.
  const closeProject = useCallback(() => {
    if (state.phase !== "loaded") return;
    // `hasActiveJobs()` (the confirm-dialog gate above) only covers a job
    // still "loading-stations"/"downloading" — a job that just finished
    // downloading but hasn't flushed its buffered apply yet (up to
    // APPLY_FLUSH_MS, see aemetJobStore.ts's flushApplies), or one sitting
    // done-but-undismissed, slips past that gate entirely. Real hole found
    // by deep-review (2026-08-29), matching cancelAll's OWN header comment
    // ("called when the project closes... so no job is left"), which this
    // was the one call site that never actually did: without this, closing
    // right in that window and opening a different project sharing a
    // sub-basin code within it lets the old project's buffered AEMET data
    // land in the new one (aemetJobStore.setContext already points at it by
    // then). Harmless no-op when nothing's pending — same as here after the
    // confirm dialog already called it once.
    aemetJobStore.cancelAll();
    const {
      projectId,
      name,
      ephemeral,
      loadedProject,
      gisLayer,
      outletPointsLayer,
      aquifersLayer,
      genericLayers,
      aemetProvenance,
      mitecoOrigin,
      observedFlow,
    } = state;
    const meta = buildProjectMeta(projectId, name, ephemeral);
    const data = buildProjectData(projectId, loadedProject, {
      gisLayer,
      outletPointsLayer,
      aquifersLayer,
      genericLayers,
      aemetProvenance,
      mitecoOrigin,
      observedFlow,
    });
    // Real freeze reported live: at Duero scale (1728 sub-basins, decades of
    // daily data per series), this used to always write `series` too
    // (`saveProject`'s own default) even when it hadn't changed since the
    // debounced autosave below already wrote it — a multi-second
    // structured-clone + IndexedDB write blocking the button from ever
    // reacting, on every single "Close project" click. Same
    // `seriesDirtyRef` the autosave already uses to skip this exact
    // rewrite; reset it here too on success so a project closed and
    // reopened right after doesn't pay for the same skip logic to catch up.
    const includeSeries = seriesDirtyRef.current;
    saveProject(meta, data, { includeSeries })
      .then(() => {
        if (includeSeries) seriesDirtyRef.current = false;
        return driveSyncStore.queueSync(meta, data);
      })
      .catch((err) => console.error("Could not save before closing the project:", err))
      .finally(() => {
        setState({ phase: "initial" });
        reloadSavedProjects();
      });
  }, [state, reloadSavedProjects]);

  // Entry point for TopBar's "Close project" button: warns first if an
  // AEMET download is still running instead of closing straight away (see
  // ConfirmCloseWithJobsDialog.tsx) — closeProject() itself stays the
  // no-questions-asked version, also used once the warning's been confirmed.
  const requestCloseProject = useCallback(() => {
    if (aemetJobStore.hasActiveJobs()) {
      setConfirmCloseWithJobs(true);
    } else {
      closeProject();
    }
  }, [closeProject]);

  // Browser Back/Forward: the address bar already changed by the time this
  // fires, so this only has to make `state` catch up to it. Depends on
  // `closeProject`/`openSaved` (not `[]`) rather than reading them through
  // stateRef: `closeProject` closes over its OWN `state` (it depends on
  // `[state, reloadSavedProjects]`), so a listener registered once with
  // empty deps would keep calling a stale `closeProject` that still thinks
  // nothing is loaded — re-subscribing on identity change is the simple fix
  // (cheap: addEventListener/removeEventListener on `window`, not a hot
  // path). Deliberately calls the plain `closeProject` (immediate save, no
  // confirmation), not `requestCloseProject` — the AEMET-jobs-running
  // warning only fires from the explicit "Close project" button; nothing is
  // lost here (the save still happens), only that one warning is skipped.
  useEffect(() => {
    function onPopState() {
      const urlId = projectIdFromLocation();
      const current = stateRef.current;
      const currentId = current.phase === "loaded" ? current.projectId : null;
      if (urlId === currentId) return;
      closeProject(); // no-op if nothing is loaded
      if (urlId !== null) openSaved(urlId);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [closeProject, openSaved]);

  /**
   * Tracks whether `loadedProject.series` has changed since it was last
   * WRITTEN to IndexedDB (not since the last render) — `true` initially so
   * a freshly opened/created project's first autosave always writes it at
   * least once. Feeds the debounced autosave's `includeSeries` below (see
   * storage.ts's `saveProject` for why this split exists and the invariant
   * it relies on). Deliberately its own tiny effect, separate from the
   * autosave one below: this one only needs to react to `series`'s own
   * reference changing, not to every field the autosave itself tracks.
   */
  const seriesDirtyRef = useRef(true);
  const seriesForDirtyCheck = state.phase === "loaded" ? state.loadedProject.series : null;
  useEffect(() => {
    seriesDirtyRef.current = true;
  }, [seriesForDirtyCheck]);

  // Deliberately does NOT depend on the whole `state` for the autosave
  // below: only what's really "the project" should reset its delay —
  // opening a popover or selecting a results row shouldn't compete with it.
  const projectToSave = state.phase === "loaded" ? state.loadedProject.project : null;
  const gisLayerToSave = state.phase === "loaded" ? state.gisLayer : null;
  const outletPointsLayerToSave = state.phase === "loaded" ? state.outletPointsLayer : null;
  const aquifersLayerToSave = state.phase === "loaded" ? state.aquifersLayer : null;
  const genericLayersToSave = state.phase === "loaded" ? state.genericLayers : null;
  const aemetProvenanceToSave = state.phase === "loaded" ? state.aemetProvenance : null;
  const mitecoOriginToSave = state.phase === "loaded" ? state.mitecoOrigin : null;
  const observedFlowToSave = state.phase === "loaded" ? state.observedFlow : null;

  // Autosave: any real change in the project (not which panel is open, nor
  // the map selection, nor whether a dialog is open — those are ephemeral,
  // not saved) gets flushed to IndexedDB with a small delay, to avoid
  // writing on every keystroke. `closeProject` does its own immediate save
  // separately, without waiting for this debounce. `loadedProject.series`
  // is deliberately not among the dependencies below — correcting an
  // earlier version of this comment that claimed it never changes from the
  // UI, which is false (AEMET/ROCIO fetches and the "Series de entrada"
  // panel both replace it). It's left out anyway because every real path
  // that changes `series` ALSO changes `project` (or `aemetProvenance`) in
  // the very same state update — updating a sub-basin's precipCol/etpCol/
  // tempCol/pumpingCol index alongside its series, see
  // EditSubBasinDialog.tsx's `onDeleteSeries`/`PasteDailySeries.tsx`'s
  // `onIndexChange`, or recording the fetch's own provenance — so this
  // effect still always re-fires when `series` does, just via one of its
  // OTHER dependencies. `seriesDirtyRef` above is a separate, narrower
  // tracker of the same underlying fact, feeding `saveProject`'s own
  // `includeSeries` instead.

  //
  // `projectId`/`name`/`ephemeral` are deliberately NOT read from this
  // effect's own closure at fire time — they're read from `stateRef`
  // instead, inside the timer callback. Renaming (or anything else that
  // only changes one of those 3 fields) doesn't reset this debounce on
  // purpose, but an already-armed timer that fired with a stale `name`
  // from before the rename would silently revert it back in IndexedDB (and
  // propagate that reversion to Drive) — a real bug this once caused.
  // Reading them fresh from `stateRef.current` at fire time fixes it
  // without changing what resets the debounce.
  useEffect(() => {
    if (state.phase !== "loaded") return;
    const { loadedProject, gisLayer, outletPointsLayer, aquifersLayer, genericLayers } = state;
    const { aemetProvenance, mitecoOrigin, observedFlow } = state;
    const immediate = forceImmediateSaveRef.current;
    forceImmediateSaveRef.current = false;
    const timer = window.setTimeout(
      () => {
        const current = stateRef.current;
        if (current.phase !== "loaded") return;
        const { projectId, name, ephemeral } = current;
        const meta = buildProjectMeta(projectId, name, ephemeral);
        const data = buildProjectData(projectId, loadedProject, {
          gisLayer,
          outletPointsLayer,
          aquifersLayer,
          genericLayers,
          aemetProvenance,
          mitecoOrigin,
          observedFlow,
        });
        const includeSeries = seriesDirtyRef.current;
        saveProject(meta, data, { includeSeries })
          .then(() => {
            if (includeSeries) seriesDirtyRef.current = false;
            driveSyncStore.queueSync(meta, data);
            // Clears a previous failure once a save actually goes through —
            // see saveError's own comment (appState.ts). A no-op setState
            // (functional form, bails out to the same reference) whenever
            // there was nothing to clear.
            setState((c) => (c.phase === "loaded" && c.saveError ? { ...c, saveError: null } : c));
          })
          .catch((err) => {
            console.error("Could not autosave the project:", err);
            setState((c) =>
              c.phase === "loaded"
                ? { ...c, saveError: t("app.autosaveError", { message: translateError(err, t) }) }
                : c
            );
          });
      },
      immediate ? 0 : 1000
    );
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectToSave,
    gisLayerToSave,
    outletPointsLayerToSave,
    aquifersLayerToSave,
    genericLayersToSave,
    aemetProvenanceToSave,
    mitecoOriginToSave,
    observedFlowToSave,
  ]);

  /**
   * Manual retry for `SaveErrorBanner.tsx`'s "Reintentar" button — a plain
   * one-off save, independent of the debounce above (no reason to wait 1s
   * again on a save the user explicitly asked to retry). Always writes
   * `series` (`includeSeries: true`), unlike the debounced autosave's own
   * `seriesDirtyRef`-gated write: whatever attempt just failed may or may
   * not have included it, and there's no cheap way to tell from here which
   * — always including it is the safe default (`storage.ts`'s own "first
   * save" invariant reasoning), just a bit more to write.
   */
  const retrySave = useCallback(() => {
    const current = stateRef.current;
    if (current.phase !== "loaded") return;
    const {
      projectId,
      name,
      ephemeral,
      loadedProject,
      gisLayer,
      outletPointsLayer,
      aquifersLayer,
      genericLayers,
      aemetProvenance,
      mitecoOrigin,
      observedFlow,
    } = current;
    const meta = buildProjectMeta(projectId, name, ephemeral);
    const data = buildProjectData(projectId, loadedProject, {
      gisLayer,
      outletPointsLayer,
      aquifersLayer,
      genericLayers,
      aemetProvenance,
      mitecoOrigin,
      observedFlow,
    });
    saveProject(meta, data, { includeSeries: true })
      .then(() => {
        seriesDirtyRef.current = false;
        driveSyncStore.queueSync(meta, data);
        setState((c) => (c.phase === "loaded" ? { ...c, saveError: null } : c));
      })
      .catch((err) => {
        console.error("Could not retry saving the project:", err);
        setState((c) =>
          c.phase === "loaded"
            ? { ...c, saveError: t("app.autosaveError", { message: translateError(err, t) }) }
            : c
        );
      });
  }, [t]);

  const openFolder = useCallback(async () => {
    if (!("showDirectoryPicker" in window)) {
      setState({
        phase: "error",
        message: t("app.noFileSystemAccessApiError"),
      });
      return;
    }
    let root: FileSystemDirectoryHandle;
    try {
      root = await window.showDirectoryPicker();
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setState({ phase: "error", message: translateError(err, t) });
      return;
    }
    // With more than one .ehd in the folder (counting subfolders) there's
    // no way to guess which one the user wants (it used to just grab the
    // first one entries() returned, without even looking at subfolders) —
    // ask before continuing to load.
    // A genuine failure here (permission denied on a subfolder, a read
    // error) still reads as "no .ehd found" below, same as before — the
    // user still gets SOME error eventually (openProject()'s own, via the
    // loadFrom() fallback further down) — but logged now (deep-review,
    // 2026-08-29) so the real reason isn't lost if that generic error is
    // ever reported and needs diagnosing.
    const found = await listEhdFiles(root, root.name).catch((err) => {
      console.error("Could not list .ehd files:", err);
      return [];
    });
    if (found.length > 1) {
      setChoosingEhd(found);
      return;
    }
    if (found.length === 1) {
      const [chosen] = found;
      await loadFrom(chosen.folder, chosen.projectName, false, undefined, chosen.fileName);
      return;
    }
    // No .ehd: this folder might still be a CSV-only project (see
    // importCsvProject.ts) — a folder with neither still gets openProject()'s
    // usual error, via the plain loadFrom() fallback below.
    if (await hasCsvProjectFiles(root)) {
      await loadFromCsv(root, root.name, false);
      return;
    }
    await loadFrom(root, root.name, false);
  }, [loadFrom, loadFromCsv, t]);

  const chooseEhd = useCallback(
    (chosen: FoundEhd) => {
      setChoosingEhd(null);
      loadFrom(chosen.folder, chosen.projectName, false, undefined, chosen.fileName);
    },
    [loadFrom]
  );

  // Examples are marked `ephemeral`: they autosave like any other project
  // (same loadFrom, no forked flow) but don't count as really saved — see
  // ProjectMeta.ephemeral in storage.ts.
  const loadExample = useCallback(
    (example: ExampleProject) =>
      // example.title is an i18n KEY (see ExampleProject in examples.ts),
      // not already-resolved text — it has to be translated here, before
      // loadFrom uses it as the project name (it's saved as-is in
      // ProjectMeta.name and shown without going through t() anywhere).
      loadFrom(example.folder(), t(example.title), true, example.adjustProject),
    [loadFrom, t]
  );

  return {
    state,
    setState,
    choosingEhd,
    setChoosingEhd,
    confirmCloseWithJobs,
    setConfirmCloseWithJobs,
    savedProjects,
    driveUnsyncedProjects,
    uploadUnsyncedToDrive,
    dismissUnsyncedDriveDialog,
    resolveDriveConflict,
    finishLoadingProject,
    openFolder,
    chooseEhd,
    openSaved,
    renameSaved,
    saveAs,
    renameCurrentProject,
    exportCurrentProject,
    exportCurrentProjectAsCsv,
    importFromFile,
    deleteCurrentProject,
    closeProject,
    requestCloseProject,
    requestImmediateSave,
    retrySave,
    loadExample,
  };
}
